import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  Button,
  message,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  DatePicker,
} from 'antd';
import {
  FilterOutlined,
  SearchOutlined,
  VerticalAlignBottomOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import ChargingHttpHandler from '../../../../shared/chargingHttpHandler';
import secureStorage from '../../../../shared/secureStorage';
import dayjs from 'dayjs';
import TargetModal from './TargetModal';

const { RangePicker } = DatePicker;

const TargetCustomer = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [form] = Form.useForm();
  const [sortedInfo, setSortedInfo] = useState({});
  const [targetForm] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [isNew, setIsNew] = useState(true);

  const httpHandler = new ChargingHttpHandler(false);
  const chargingAccessToken = secureStorage.getItem('chargingAccessToken');

  const add_search_log = async (success) => {
    const userRole = secureStorage.getItem('userRole');
    // 紀錄搜尋時間
    await httpHandler
      .axios()
      .post(
        '/platform/update_user_log_table',
        {
          log_time: dayjs().format('YYYY/MM/DD HH:mm:ss'),
          user_role: userRole,
          action_type: '目標客群搜尋',
          action_content: '用戶搜尋目標客群資訊',
          status: success,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        }
      )
      .catch((e) => {
        console.error('寫入搜尋紀錄失敗', e);
      });
  };

  const fetchTargetInfo = async () => {
    try {
      setLoading(true);

      // 同時獲取會員資料和訂單資料
      const targetResponse = await httpHandler
        .axios()
        .get('/platform/get_target_customer', {
          headers: {
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        });

      const targetData = targetResponse.data.data;

      if (targetData) {
        await add_search_log(1);
      } else {
        await add_search_log(0);
      }

      const transformedData = targetData.map((item) => ({
        id: item.id,
        name: item.customer_name,
        description: item.description,
        customer_length: item.customer_length,
        create_time: item.create_time,
      }));

      setData(transformedData);
      setFilteredData(transformedData);
    } catch (error) {
      console.error('Failed to fetch member info:', error);
      message.error('獲取客戶資料失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTargetInfo();
  }, []);

  // 刪除客群
  const handleDelete = (target_id, target_name) => {
    Modal.confirm({
      maskClosable: true, // 點擊蒙層是否允許關閉
      title: `確定刪除 ${target_name}？`,
      icon: <QuestionCircleOutlined />,
      footer: (_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          <OkBtn loading={loading} />
        </>
      ),
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        setLoading(true);
        return httpHandler
          .axios()
          .get(`/platform/delete_target_customer?target_id=${target_id}`, {
            headers: {
              Authorization: `Bearer ${chargingAccessToken}`,
            },
          })
          .then((response) => {
            if (response.data.success) {
              message.success('刪除客群成功');
            } else {
              console.log(response.message);
            }
            fetchTargetInfo();
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };

  // 下載範例檔
  const DownloadSample = () => {
    const link = document.createElement('a');
    link.href = '/客群名單範例.csv'; // public 資料夾下的檔案路徑
    link.setAttribute('download', '客群名單範例.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 應用篩選邏輯
  const applyFilters = (values) => {
    let filtered = [...data];

    // 操作時間篩選
    if (values.createDate && values.createDate.length === 2) {
      const [startDate, endDate] = values.createDate;
      filtered = filtered.filter((item) => {
        const createDate = dayjs(item.create_time);
        return createDate.isBetween(startDate, endDate, 'null', '[]');
      });
    }

    // 客群名稱篩選
    if (values.name) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(values.name.toLowerCase())
      );
    }

    // 客群描述篩選
    if (values.description) {
      filtered = filtered.filter((item) =>
        item.description
          ?.toLowerCase()
          .includes(values.description?.toLowerCase())
      );
    }

    return filtered;
  };

  const handleAdvancedFilter = (values) => {
    const result = applyFilters(values);
    setFilteredData(result);
    setFilters(values);
    setFilterVisible(false);
    setSortedInfo({}); // 清除排序狀態
    message.success(`篩選完成，共 ${result.length} 筆資料`);
  };

  // 清除篩選
  const clearFilters = () => {
    setFilteredData(data);
    setFilters({});
    form.resetFields();
    setSearch('');
    setSortedInfo({});
    fetchTargetInfo();
  };

  const columns = [
    {
      title: '客群ID',
      dataIndex: 'id',
      hidden: true,
    },
    {
      title: '客群名稱',
      dataIndex: 'name',
    },
    {
      title: '客群描述',
      dataIndex: 'description',
    },
    {
      title: '名單數量',
      dataIndex: 'customer_length',
    },
    {
      title: '建立時間',
      dataIndex: 'create_time',
      render: (dateString) => {
        const date = dayjs(dateString).add(8, 'hours');

        return date.format('YYYY-MM-DD A hh:mm');
      },
    },
    {
      title: '操作',
      dataIndex: '',
      width: 120,
      render: (record) => {
        return (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setIsNew(false);
                setModalVisible(true);
                targetForm.setFieldsValue({
                  id: record.id,
                  customerName: record.name,
                  customerDescription: record.description,
                });
              }}>
              修改
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                handleDelete(record.id, record.name);
              }}>
              刪除
            </Button>
          </div>
        );
      },
    },
  ];

  // 簡單搜尋功能（保留原有的搜尋框功能）
  const handleSearch = () => {
    setSortedInfo({}); // 清除排序狀態
    console.log(search.toLowerCase());
    if (!search.trim()) {
      setFilteredData(data);
      return;
    }
    const filtered = data.filter(
      (item) =>
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description?.toLowerCase().includes(search?.toLowerCase())
    );
    setFilteredData(filtered);
  };

  return (
    <div
      style={{
        background: '#f7faf9',
        padding: 15,
        borderRadius: 16,
        marginTop: 24,
      }}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
        <Input
          placeholder="搜尋客群名稱"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={handleSearch}
          style={{ maxWidth: 350 }}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
          搜尋
        </Button>
        <Button icon={<VerticalAlignBottomOutlined />} onClick={DownloadSample}>
          下載客群範例
        </Button>
        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            setIsNew(true);
            setModalVisible(true);
          }}>
          新增客群
        </Button>
        <Button
          icon={<FilterOutlined />}
          onClick={() => setFilterVisible(true)}>
          篩選
        </Button>
        <Button onClick={clearFilters}>清除</Button>
      </div>

      {/* 篩選條件顯示 */}
      {Object.keys(filters).length > 0 && (
        <div style={{ marginBottom: 12, fontSize: 12, color: '#666' }}>
          篩選條件:
          {Object.entries(filters)
            .filter(([key, value]) => value && value.toString().trim() !== '')
            .map(([key, value]) => {
              const fieldLabels = {
                createDate: '建立時間',
                name: '客群名稱',
                description: '客群描述',
              };
              const fieldLabel = fieldLabels[key] || key;
              return (
                <Tag
                  key={key}
                  closable
                  onClose={() => {
                    const newFilters = { ...filters };
                    delete newFilters[key];
                    setFilters(newFilters);
                    const result = applyFilters(newFilters);
                    setFilteredData(result);
                  }}>
                  {fieldLabel}
                </Tag>
              );
            })}
        </div>
      )}

      <div style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>
        共 {filteredData.length} 筆資料
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`,
          position: ['bottomCenter'],
        }}
        bordered={false}
        loading={loading}
        style={{ background: '#fff', borderRadius: 12 }}
      />

      <TargetModal
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        targetForm={targetForm}
        isNew={isNew}
        fetchTargetInfo={fetchTargetInfo}
      />
      {/* 進階篩選 Modal */}
      <Modal
        title="進階篩選"
        open={filterVisible}
        onCancel={() => setFilterVisible(false)}
        width={800}
        footer={[
          <Button
            key="clear"
            onClick={() => {
              form.resetFields();
              setFilters({});
              setFilteredData(data);
            }}>
            清除所有
          </Button>,
          <Button key="cancel" onClick={() => setFilterVisible(false)}>
            取消
          </Button>,
          <Button key="search" type="primary" onClick={() => form.submit()}>
            搜尋
          </Button>,
        ]}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAdvancedFilter}
          initialValues={filters}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="建立時間" name="createDate">
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={10}>
              <Form.Item label="客群名稱" name="name">
                <Input placeholder="客群名稱" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label="客群描述" name="description">
                <Input placeholder="客群描述" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default TargetCustomer;
